import { useState, useCallback } from "react";
import { Button, Input, Label } from "@cieda/djayma-ui";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { useCreateMovement } from "@/hooks/admin/useInventory";
import type { InventoryProduct } from "@/lib/admin-api/inventory";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface RestockModalProps {
  readonly product: InventoryProduct | null;
  readonly onClose: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function RestockModal({ product, onClose }: RestockModalProps) {
  const [quantity, setQuantity] = useState(1);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const createMovement = useCreateMovement();

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!product) return;

      if (quantity <= 0) {
        setError("La quantité doit être supérieure à zéro.");
        return;
      }
      if (!reason.trim()) {
        setError("La raison est obligatoire.");
        return;
      }

      setError(null);

      await createMovement.mutateAsync({
        product_id: product.id,
        type: "restock",
        quantity,
        reason: reason.trim(),
      });

      setQuantity(1);
      setReason("");
      onClose();
    },
    [product, quantity, reason, createMovement, onClose],
  );

  const handleQuantityChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setQuantity(Number(e.target.value));
    },
    [],
  );

  const handleReasonChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setReason(e.target.value);
    },
    [],
  );

  if (!product) return null;

  return (
    <AdminModal
      open={!!product}
      onClose={onClose}
      title="Réapprovisionner le stock"
      size="sm"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Product info */}
        <div className="rounded-lg border border-stone-200 bg-stone-50 px-3 py-2">
          <p className="text-sm font-medium text-stone-800">{product.name}</p>
          <p className="text-xs text-stone-500">
            SKU : {product.sku} — Stock actuel :{" "}
            <span className="font-semibold text-stone-700">
              {product.current_stock}
            </span>
          </p>
        </div>

        {/* Quantity */}
        <div>
          <Label htmlFor="restock-quantity">Quantité à ajouter</Label>
          <Input
            id="restock-quantity"
            type="number"
            min={1}
            step={1}
            value={quantity}
            onChange={handleQuantityChange}
          />
        </div>

        {/* Reason */}
        <div>
          <Label htmlFor="restock-reason">Raison</Label>
          <Input
            id="restock-reason"
            type="text"
            placeholder="Ex. Livraison fournisseur du 24/03"
            value={reason}
            onChange={handleReasonChange}
          />
        </div>

        {/* Error */}
        {error && (
          <p className="text-xs text-red-600">{error}</p>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 pt-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onClose}
          >
            Annuler
          </Button>
          <Button
            type="submit"
            size="sm"
            disabled={createMovement.isPending}
          >
            {createMovement.isPending ? "Enregistrement…" : "Réapprovisionner"}
          </Button>
        </div>
      </form>
    </AdminModal>
  );
}
